/**
 * Automated-traffic detection for analytics suppression.
 *
 * Every destination in this layer is fed from client-side JavaScript, so any crawler, prerender
 * service, uptime monitor, or browser automation run that executes the app also produces page views
 * and click events. GA4 drops IAB-listed crawlers on ingestion, but the Pendo page-hit transport
 * (`services/clientportal-analytics.service.ts`) has no such filter, so unfiltered bot traffic lands
 * in the product page-hit indices as real member activity.
 *
 * Detection is deliberately conservative: a false positive silences analytics for a real member,
 * which is worse than admitting a bot that hid its identity. Only self-declared automation
 * (user-agent tokens) and automation the browser itself reports (`navigator.webdriver`, harness
 * globals) count.
 */

/**
 * Self-describing automation names. Matched anywhere in the user agent because each is long enough
 * that a substring hit is already conclusive (`HeadlessChrome`, `Chrome-Lighthouse`).
 */
const AUTOMATION_NAME_PATTERN =
  /headless|lighthouse|pagespeed|phantomjs|puppeteer|playwright|selenium|webdriver|cypress|prerender|rendertron|pingdom|gtmetrix|statuscake|uptimerobot|newrelicpinger|facebookexternalhit/i

/**
 * Short crawler tokens, which appear as name suffixes (`Googlebot`, `AhrefsBot`, `Yahoo! Slurp`).
 * A trailing boundary is required so device model names that merely contain the token
 * (`CUBOT_NOTE_20`) are not mistaken for crawlers.
 */
const CRAWLER_TOKEN_PATTERN = /(?:bot|crawler|crawl|spider|slurp)(?![a-z0-9_])/i

/**
 * Device families whose model name ends in a bot-like token. CUBOT handsets are the widely reported
 * case (`CUBOT MAX`), and dropping the token before matching keeps those real members tracked.
 */
const FALSE_POSITIVE_DEVICE_PATTERN = /cubot/gi

export interface AnalyticsBotSignals {
  /** `navigator.userAgent`, or the request user agent when classifying server-side. */
  userAgent?: string | null
  /** `navigator.webdriver` — true under Playwright, Puppeteer, and Selenium. */
  webdriver?: boolean
  /** A test-harness global on `window` (e.g. Cypress, PhantomJS) was found. */
  automationGlobal?: boolean
}

/** True when the supplied signals identify automated traffic that must not reach any provider. */
export function isAnalyticsBotTraffic({ userAgent, webdriver, automationGlobal }: AnalyticsBotSignals): boolean {
  if (webdriver === true || automationGlobal === true) return true

  const normalized = (userAgent ?? '').replace(FALSE_POSITIVE_DEVICE_PATTERN, '')
  return AUTOMATION_NAME_PATTERN.test(normalized) || CRAWLER_TOKEN_PATTERN.test(normalized)
}

/** Automation globals installed on `window` by harnesses that do not set `navigator.webdriver`. */
const AUTOMATION_WINDOW_KEYS = ['Cypress', 'callPhantom', '_phantom', '__nightmare'] as const

/** Reads browser bot signals. Returns empty signals outside a browser so callers stay SSR-safe. */
export function readAnalyticsBotSignals(): AnalyticsBotSignals {
  if (typeof navigator === 'undefined') return {}

  const automationGlobal =
    typeof window !== 'undefined' && AUTOMATION_WINDOW_KEYS.some(key => key in (window as unknown as Record<string, unknown>))

  return {
    userAgent: navigator.userAgent,
    webdriver: navigator.webdriver === true,
    automationGlobal,
  }
}
